import React from 'react';
import { styled } from 'linaria/react';

import AddToCartFormik from '@jetshop/core/cart/AddToCartFormik';
import { useNotification } from '@jetshop/core/components/Notifications';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { Price } from '@jetshop/ui/Price';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';

import addToCartMutation from '../../../Cart/queries/addToCart.gql';
import CartQuery from '../../../Cart/queries/cartQuery.gql';
import ProductToastWrapper from './ProductToast';
import { VariantSelector } from './VariantSelector';
import { useProductValidationMessage } from '../../../ProductPage/useProductValidationMessage';
import { AddToCartQtyButton } from './AddToCartQtyButton';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { Favourite } from '../../../ProductList/Favourite';
import { PriceHistoryWrapper } from '../../PriceHistory';

const QuantityAddToCartWrapper = styled('div')`
  display: flex;
  width: 100%;
  align-items: flex-end;
  margin-bottom: 15px;
`;

const FavouriteWrapper = styled('div')`
  padding: 0.5em 0 0.5em 0.5em;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const StyledPrice = styled(Price)`
  font-size: 28px;
  font-weight: bold;
  display: flex;
  align-items: center;
  line-height: 1;
  .new-price {
    color: #c94343;
  }
  .old-price {
    margin-left: 5px;
    font-size: 18px;
    color: #868686;
  }
`;

export const PriceStockWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 5px;

  .stock-status-indicator {
    display: flex;
    align-items: center;
    .icon {
      margin-left: 5px;
    }
    .title {
      font-weight: bold;
      text-transform: uppercase;
      margin-right: 4px;
      color: #282828;
      font-size: 12px;
      font-weight: 700;
      line-height: 139.5%; /* 16.74px */
      letter-spacing: 0.48px;
    }
    span {
      color: #282828;
      font-size: 14px;
      font-weight: 400;
      margin-right: 3px;
    }
  }
`;

function AddToCartForm({
  product,
  variant,
  getMissingOptions,
  variantHandler
}) {
  // GET DYNAMIC PRICE
  const price = useDynamicPrice(product, variant);

  // GET STOCK STATUS
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus?.status === 'notifyWhenBack';

  // CHECK VARAINTS AND PRICE
  const { hasVariants, hidePrice } = product;

  // GET VALIDATIONS
  const { validation: variantValidation, missingOptions } = variantHandler;

  // VALIDATION MESSAGES
  const { validationMessage } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });

  // TRIGGER NOTIFICATION
  const [trigger, dismiss] = useNotification();

  // HANDLER FOR ADDING TO CART
  const onAddToCartInit = ({ mutationId, quantity }) => {
    trigger(
      <ProductToastWrapper
        selectedVariation={variant}
        product={product}
        quantity={quantity}
      />,
      {
        id: mutationId,
        type: 'add-to-cart'
      }
    );
  };
  // HANDLER FOR ADDING TO CART ERROR
  const onAddToCartError = () => {
    return ({ mutationId, quantity, error }) => {
      dismiss(mutationId);

      trigger(
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={quantity}
          error={error}
        />,
        {
          type: 'add-to-cart'
        }
      );
    };
  };

  return (
    <AddToCartFormik
      onAddToCartInit={onAddToCartInit}
      onAddToCartError={onAddToCartError}
      cartQuery={CartQuery}
      cartMutation={addToCartMutation}
      product={product}
      variant={variant}
      getMissingOptions={getMissingOptions}
    >
      {() => {
        return (
          <div>
            {product.hasVariants && (
              <VariantSelector
                product={product}
                variantHandler={variantHandler}
                showValidation={!!validationMessage}
              />
            )}

            <PriceStockWrapper>
              <div>
                <StyledPrice {...price} hidePrice={hidePrice} />
                {!hidePrice &&
                  price.price.incVat < price.previousPrice.incVat && (
                    <PriceHistoryWrapper
                      articleNumber={product.articleNumber}
                      variant={variant}
                    />
                  )}
              </div>
              {stockStatus?.status && (
                <StockStatusIndicator
                  status={stockStatus.status}
                  text={stockStatus.text}
                />
              )}
            </PriceStockWrapper>
            <QuantityAddToCartWrapper>
              <AddToCartQtyButton
                outOfStockNotify={outOfStockNotify}
                product={product}
                variant={variant}
                productPage={true}
                validationMessage={validationMessage}
              />
              <FavouriteWrapper>
                <Favourite product={product} />
              </FavouriteWrapper>
            </QuantityAddToCartWrapper>
          </div>
        );
      }}
    </AddToCartFormik>
  );
}

export default AddToCartForm;
